import { expandDisplayFieldTemplate } from './expand-display-field-template';

/**
 * Returns a function that resolves an id to a display label by looking it up
 * in a reactive options list. Designed for `hlm-select` value templates and
 * `hlm-select-multiple` `[itemToString]` bindings, where the form control
 * stores ids but the UI needs the human-readable label.
 *
 * The label is built from a `front.displayField` template — same shape and
 * expansion rules as `ToOptionsPipe` (see `expandDisplayFieldTemplate`):
 * identifier shape (`'localName'`) is equivalent to `'{localName}'`; a
 * multi-placeholder template (`'{code} - {name}'`) interpolates every field.
 * Defaults to `'{name}'`.
 *
 * @example
 * // Default — T must have `name: string | null | undefined` (a nullish name expands to '')
 * protected readonly clientItemToString = itemLabelOf(this.clientOptions);
 *
 * @example
 * // Custom label field
 * protected readonly langItemToString = itemLabelOf(this.langOptions, 'localName');
 *
 * @example
 * // Template label
 * protected readonly permissionItemToString = itemLabelOf(this.permissionOptions, '{code} - {name}');
 */
export function itemLabelOf<T extends { id: string; name?: string | null }>(
  options: () => T[],
): (id: string | null) => string;
export function itemLabelOf<T extends { id: string }>(
  options: () => T[],
  template: string,
): (id: string | null) => string;
export function itemLabelOf<T extends { id: string }>(
  options: () => T[],
  template: string = '{name}',
): (id: string | null) => string {
  return (id) => {
    if (!id) return '';
    const item = options().find((option) => option.id === id);
    if (!item) return '';
    return expandDisplayFieldTemplate(template, item as Record<string, unknown>);
  };
}
